/**
 * Interface-language persistence.
 *
 * Persisted in localStorage, KEYED BY USER ID, so the preference survives reload and is
 * scoped to the signed-in user on that device (a guest uses the "guest" key, and the
 * preference is re-read when the signed-in user changes). This is intentionally device
 * local: cross-device sync via a profiles.locale column is a deliberate future
 * enhancement (DB-migration-gated) tracked in the capability audit, not built here.
 */
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "./types";

const KEY_PREFIX = "nuvra.locale";

function storageKey(userId: string | null): string {
  return `${KEY_PREFIX}.${userId ?? "guest"}`;
}

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === "string" && SUPPORTED_LOCALES.some((l) => l.code === value);
}

export function loadLocale(userId: string | null): Locale {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (isSupportedLocale(raw)) return raw;
  } catch {
    /* localStorage unavailable (SSR/private mode) - fall through to default */
  }
  return DEFAULT_LOCALE;
}

export function saveLocale(userId: string | null, locale: Locale): void {
  try {
    localStorage.setItem(storageKey(userId), locale);
  } catch {
    /* best effort; an unwritable store should never crash the UI */
  }
}
